// PostHog analytics initialization
// Privacy-first: cookieless mode, no PII, identified profiles only
// Lazy-loaded: PostHog JS is ~200KB, so we dynamically import it after first render

import type { PostHog } from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string) || 'https://us.i.posthog.com';

let posthogInstance: PostHog | null = null;
let initPromise: Promise<PostHog | null> | null = null;

/**
 * Initialize PostHog with privacy-first configuration (lazy-loaded).
 * - Cookieless: persistence in memory only (no cookies, no localStorage)
 * - Identified only: anonymous users are not tracked as persons
 * - PII denylist: sensitive fields are never sent as properties
 */
export function initPostHog(): Promise<PostHog | null> {
  if (!POSTHOG_KEY) {
    if (import.meta.env.DEV) {
      console.warn('[PostHog] VITE_POSTHOG_KEY not set — analytics disabled');
    }
    return Promise.resolve(null);
  }

  if (initPromise) return initPromise;

  initPromise = import('posthog-js').then(({ default: posthog }) => {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      persistence: 'memory',
      person_profiles: 'identified_only',
      capture_pageview: false, // We handle page views manually via usePageTracking
      capture_pageleave: true,
      autocapture: false, // Explicit events only — no accidental PII capture
      property_denylist: [
        'email',
        'phone',
        'password',
        'token',
        'access_token',
        'refresh_token',
        'api_key',
        'secret',
        'credit_card',
        'ssn',
        'address',
        'full_name',
        'first_name',
        'last_name',
      ],
      disable_session_recording: true, // No session recordings — privacy first
      opt_out_capturing_by_default: false,
      loaded: () => {
        if (import.meta.env.DEV) {
          console.warn('[PostHog] Initialized (dev mode)');
        }
      },
    });
    posthogInstance = posthog;
    return posthog;
  });

  return initPromise;
}

/**
 * Get the PostHog instance if already loaded. Returns null if not yet initialized.
 */
export function getPostHog(): PostHog | null {
  return posthogInstance;
}

/**
 * Identify a user after authentication.
 * Only sends non-PII properties.
 */
export function identifyUser(userId: string, properties?: Record<string, string | number | boolean>) {
  if (!POSTHOG_KEY) return;
  posthogInstance?.identify(userId, properties);
}

/**
 * Reset PostHog identity on logout.
 */
export function resetUser() {
  if (!POSTHOG_KEY) return;
  posthogInstance?.reset();
}

// Backwards-compatible export — lazy consumers should use getPostHog()
export { posthogInstance as posthog };
